/**
 * ErrorBoundary — catches unhandled render errors anywhere in the tree.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 * Shows a minimal recovery UI with a reload button instead of a blank page.
 * Does NOT catch errors in event handlers or async code (use try/catch for those).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console for debugging; swap for a monitoring service in production
    console.error('[ErrorBoundary] Unhandled render error:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-6 p-6 text-white">
          <div className="flex flex-col items-center gap-3 max-w-md text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center text-2xl">
              ⚠️
            </div>
            <h1 className="text-xl font-bold">Une erreur inattendue est survenue</h1>
            <p className="text-sm text-white/50">
              L'application a rencontré un problème. Vous pouvez recharger la page pour reprendre votre travail — vos modifications sont sauvegardées automatiquement.
            </p>
            {this.state.error && (
              <details className="w-full mt-2">
                <summary className="text-xs text-white/30 cursor-pointer hover:text-white/50 transition-colors">
                  Détails techniques
                </summary>
                <pre className="mt-2 text-left text-xs bg-white/5 border border-white/10 rounded-lg p-3 overflow-auto max-h-40 text-red-300/70">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-4 py-2 rounded-xl border border-white/15 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-all"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition-all"
            >
              Recharger la page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
